<template>
    <div class="user-permission-modal-table-wrapper">
        <Button
            v-if="!disabled"
            class="user-permission-modal-add-client"
            type="text"
            icon="md-person-add"
            data-testid="add-brand__button"
            @click="addNewClientRow"
        >
            Add Brand
        </Button>

        <Table
            :row-class-name="rowClassName"
            :columns="columns"
            :data="tableData"
            :show-header="!preview"
            class="user-permission-modal-table user-permission-modal-table--ghost"
        />
    </div>
</template>

<script>
import { resolveComponent } from "vue";

import CampaignSelect from "@/components/ManageUsers/CampaignSelect.vue";
import ClientSelect from "@/components/ManageUsers/ClientSelect.vue";

import ClientsQuery from "@/apollo/queries/Clients.gql";

import bus from "@/bus";
import { deepClone } from "@/utils";

export default {
    name: "UserScopeSelect",
    props: {
        clients: {
            type: Object,
            required: true
        },

        clientCampaigns: {
            type: Object,
            required: true
        },

        data: {
            type: Array,
            required: true
        },

        disabled: {
            type: Boolean,
            default: false
        },

        preview: {
            type: Boolean,
            default: false
        },

        showEmptyRow: {
            type: Boolean,
            default: false
        }
    },

    emits: ["update:modelValue", "remove-client-scope", "user-scope-update", "input"],

    data() {
        return {
            allClients: [],
            columns: [
                {
                    title: "Brand",
                    render: this.renderClientCell.bind(this),
                    className: "bordered-column client-name",
                    width: 120,
                    ellipsis: true
                },

                {
                    title: "Campaigns",
                    render: this.renderCampaignCell.bind(this),
                    className: "bordered-column client-campaigns",
                    width: 300,
                    ellipsis: true
                },
                {
                    title: " ",
                    align: "right",
                    className: "client-campaigns",
                    render: this.renderActionCell.bind(this),
                    width: 33
                }
            ],
            newRowData: {
                newRow: true,
                clientId: null,
                scopes: []
            },
            newRows: [],
            scope: {}
        };
    },

    computed: {
        allClientsShown() {
            return Object.keys(this.clients).length + this.newRows.length >= this.allClients.length;
        },

        scopesToSave() {
            return Object.keys(this.scope).reduce((acc, clientId) => acc.concat(this.scope[clientId]), []);
        },

        tableData() {
            return this.newRows.concat(this.data);
        }
    },

    watch: {
        clients: {
            handler(clients) {
                const ids = Object.keys(clients);
                this.newRows = this.newRows.filter(r => !ids.includes(r.clientId));
            }
        }
    },

    created() {
        this.$apollo.queries.clients.refetch();

        if (!this.data.length && this.showEmptyRow) {
            this.newRows.push(deepClone(this.newRowData));
        }
    },

    methods: {
        addNewClientRow() {
            if (this.allClientsShown) {
                this.$snackbar.info("This user has access to all campaigns");
            } else {
                this.newRows.push(deepClone(this.newRowData));
            }
        },

        isClientDisabled(clientId) {
            return this.disabled || !this.$auth.hasScope({ clientId });
        },

        newRowToValue(scopes) {
            if (!scopes) {
                return [];
            }

            return scopes.map(scope => {
                if (typeof scope.campaignId === "undefined") {
                    return "all";
                }

                return {
                    clientId: scope.clientId,
                    _id: scope.campaignId
                };
            });
        },

        onUserScopeUpdate(data) {
            this.$emit("user-scope-update", data);
        },

        onNewRowClientChange(rowIndex, selectedClient) {
            this.newRows[rowIndex].clientId = selectedClient;
        },

        onScopeChange(clientId, newScope) {
            this.scope[clientId] = this.rowToScope(clientId, newScope);

            this.$emit("update:modelValue", this.scopesToSave);
        },

        removeClientScope(clientId, isNewRow) {
            if (isNewRow) {
                this.newRows = this.newRows.filter(r => r.clientId !== clientId);
                return;
            }

            this.$emit("remove-client-scope", clientId);
        },

        removeNewClientRow(rowIndex) {
            const removed = this.newRows.splice(rowIndex, 1);

            if (removed[0].clientId) {
                delete this.scope[removed[0].clientId];
            }

            this.$emit("input", this.scopesToSave);
        },

        renderClientCell(h, params) {
            if (params.row.newRow) {
                return h(ClientSelect, {
                    excludedIds: Object.keys(this.clients).concat(this.newRows.map(r => r.clientId).filter(Boolean)),
                    modelValue: params.row.clientId,
                    transfer: true,
                    size: "small",
                    onChange: this.onNewRowClientChange.bind(this, params.index)
                });
            }

            return h("span", params.row.name);
        },

        renderCampaignCell(h, params) {
            if (params.row.newRow) {
                if (params.row.clientId) {
                    return h(CampaignSelect, {
                        clientId: params.row.clientId,
                        modelValue: this.newRowToValue(this.scope[params.row.clientId]),
                        onInput: this.onScopeChange.bind(this, params.row.clientId),
                        onUserScopeUpdate: this.onUserScopeUpdate.bind(this)
                    });
                }

                return h("span", {
                    class: "select-client-notice",
                    innerHTML: "Please select the brand"
                });
            }

            return h(CampaignSelect, {
                title: params.row.name,
                transfer: true,
                preview: this.preview,
                clientId: params.row._id,
                disabled: this.disabled,
                modelValue: this.clientCampaigns[params.row._id],
                onUserScopeUpdate: this.onUserScopeUpdate.bind(this)
            });
        },

        renderActionCell(h, params) {
            if (this.isClientDisabled(params.row._id)) {
                return null;
            }

            const Icon = resolveComponent("Icon");

            return h(Icon, {
                class: "remove-client-scope",
                title: params.row.name,
                "data-testid": "remove-client-scope__button",
                type: "md-close",
                onClick: params.row.newRow
                    ? this.removeNewClientRow.bind(this, params.index)
                    : this.removeClientScope.bind(this, params.row._id, params.row.newRow)
            });
        },

        rowClassName(row) {
            if (row.newRow) {
                return "new-entry-row";
            }

            return "";
        },

        rowToScope(clientId, row) {
            return row
                .map(r => {
                    if (typeof r === "object") {
                        return {
                            clientId,
                            ...(!r.clientScope && { campaignId: r._id })
                        };
                    }

                    return null;
                })
                .filter(Boolean);
        }
    },

    apollo: {
        clients: {
            query: ClientsQuery,
            variables() {
                return {
                    orderBy: "name_ASC",
                    wppProjectId: this.$store.state.auth?.wppProjectId
                };
            },
            error(e) {
                bus.$emit("apolloErrorEvent", e);
            },
            result({ data: { clients } }) {
                this.isLoading = false;
                this.allClients = clients;
            }
        }
    }
};
</script>
